import React, { useState, useEffect, useCallback } from 'react';
import { useCan, useNavigation } from '@refinedev/core';
import {
  Table,
  Space,
  Tag,
  Card,
  Form,
  Input,
  Select,
  DatePicker,
  Button,
  Row,
  Col,
  Typography,
  Statistic,
  Tabs,
  Tooltip,
  Modal,
  Switch,
  Popconfirm,
  message,
} from 'antd';
import {
  SearchOutlined,
  ClearOutlined,
  ShoppingOutlined,
  UserOutlined,
  EyeOutlined,
  CreditCardOutlined,
  BarChartOutlined,
  PlusOutlined,
  DeleteOutlined,
  InfoCircleOutlined,
  SyncOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import { adminApi } from '@/services/api';
import { ExportButton } from '@/components/ExportButton';
import { exportOptions } from '@/constants/exportOptions';
import {
  AutoRefreshToggle,
  AnalyticsDateRangeFilter,
  type AnalyticsPeriod,
  type AnalyticsDateRange,
  type AnalyticsShownRange,
} from '@/components';
import { useAutoRefresh } from '@hooks/useAutoRefresh';
import { useLatestRequest } from '@hooks/useLatestRequest';
import { formatMoney } from '@utils/formatMoney';
import { apiErrorMessage } from '@utils/apiError';
import { formatIstRefundTime, formatStorePrice } from '@utils/storePrice';
import { analyticsWindow } from '@utils/analyticsWindow';
import type {
  Purchase,
  PurchaseTester,
  RevenueAnalyticsResponse,
  RevenueTimeSeries,
  RevenueTopProduct,
  ActiveSubscription,
  SubscriptionSummary,
} from '@/types';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const STATUS_COLORS: Record<string, string> = {
  completed: 'green',
  pending: 'orange',
  failed: 'red',
  refunded: 'purple',
  cancelled: 'default',
};

const PLATFORM_COLORS: Record<string, string> = {
  google_play: 'green',
  app_store: 'blue',
  in_app: 'cyan',
};

const CLASSIFICATION_COLORS: Record<string, string> = {
  real: 'green',
  sandbox: 'orange',
  internal_tester: 'blue',
  editor: 'default',
};

const CLASSIFICATION_LABELS: Record<string, string> = {
  real: 'Real',
  sandbox: 'Sandbox',
  internal_tester: 'Tester',
  editor: 'Editor',
};

export const PurchaseList: React.FC = () => {
  const { show, push } = useNavigation();

  // Purchases state
  const [purchases, setPurchases] = useState<Purchase[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [form] = Form.useForm();

  // Revenue state
  const [revenue, setRevenue] = useState<RevenueAnalyticsResponse | null>(null);
  const [revenueLoading, setRevenueLoading] = useState(false);
  const [revenuePeriod, setRevenuePeriod] = useState('last_30_days');
  const [revenueCustomRange, setRevenueCustomRange] = useState<[Dayjs, Dayjs] | null>(null);
  // The range and fetch time of the revenue figures on screen, for the label.
  const [revenueShown, setRevenueShown] = useState<AnalyticsShownRange | null>(null);
  const beginRevenueRequest = useLatestRequest();
  const [revenueBreakdown, setRevenueBreakdown] = useState<{
    realMoneyByCurrency: Array<{
      currency: string;
      net: number;
      previousNet: number;
      changePercent: number;
    }>;
    // Legacy scalar — preserved for the deploy bridge. Use byCurrency when present.
    realMoney: number;
    realMoneyChange: number;
    inGame: number;
    inGameChange: number;
  } | null>(null);
  // Per-classification × currency breakdown for the totals strip + drill-down.
  // Source: GET /admin/analytics/revenue-breakdown → byClassification[].
  const [revenueByClassification, setRevenueByClassification] = useState<
    Array<{
      classification: string;
      currency?: string;
      gross: number;
      refunded: number;
      net: number;
      txnCount: number;
      refundedTxnCount: number;
    }>
  >([]);
  const [classificationDrillOpen, setClassificationDrillOpen] = useState(false);

  // Subscriptions state
  const [subscriptions, setSubscriptions] = useState<ActiveSubscription[]>([]);
  const [subSummary, setSubSummary] = useState<SubscriptionSummary | null>(null);
  const [subLoading, setSubLoading] = useState(false);

  // Testers state
  const [testers, setTesters] = useState<PurchaseTester[]>([]);
  const [testersLoading, setTestersLoading] = useState(false);
  const [addTesterModal, setAddTesterModal] = useState(false);
  const [testerForm] = Form.useForm();

  // Auto-refresh state — drives the toggle in the page header.
  const [activeTab, setActiveTab] = useState('purchases');
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [refreshInterval, setRefreshInterval] = useState(30_000);

  const fetchPurchases = useCallback(
    async (p = 1, filters: Record<string, string | number | boolean> = {}, size?: number) => {
      const limit = size || pageSize;
      setLoading(true);
      try {
        const res = await adminApi.searchPurchases({
          ...filters,
          limit,
          offset: (p - 1) * limit,
          includeUserInfo: true,
        } as never);
        const data = res.data;
        setPurchases(data.purchases || data.data || (Array.isArray(data) ? data : []));
        setTotal(data.total || 0);
        setPage(p);
      } catch {
        message.error('Failed to load purchases');
      } finally {
        setLoading(false);
      }
    },
    [pageSize]
  );

  // "Sync Google refunds" runs the voided-purchase sweep now; it needs economy edit.
  const { data: canEditEconomy } = useCan({ resource: 'economy', action: 'edit' });
  const [syncingRefunds, setSyncingRefunds] = useState(false);

  const syncGoogleRefunds = async () => {
    setSyncingRefunds(true);
    try {
      const { data: counts } = await adminApi.syncGoogleVoidedPurchases();
      const notCompleted = counts.notCompleted > 0 ? `, ${counts.notCompleted} not completed` : '';
      const errors =
        counts.errors > 0 ? `, ${counts.errors} ${counts.errors === 1 ? 'error' : 'errors'}` : '';
      const text = `Refunds: ${counts.refunded} new, ${counts.alreadyRefunded} known, ${counts.notFound} not found${notCompleted}${errors}`;
      if (counts.errors > 0) message.warning(text);
      else message.success(text);
      await fetchPurchases(page, currentFilters, pageSize);
    } catch (error) {
      message.error(apiErrorMessage(error, 'Failed to sync Google refunds'));
    } finally {
      setSyncingRefunds(false);
    }
  };

  const fetchRevenue = useCallback(async () => {
    const isLatest = beginRevenueRequest();
    setRevenueLoading(true);
    const startedAt = Date.now();
    const range: AnalyticsDateRange = {
      period: revenuePeriod as AnalyticsPeriod,
      customRange: revenueCustomRange ?? undefined,
    };
    try {
      // One IST window for all three calls, computed here, so the stats, the
      // revenue cards and the classification strip cover the same instants as
      // the label even if this clock and the server's disagree near midnight.
      // The revenue stats need it anyway: without fromDate/toDate the endpoint
      // falls back to the last 30 days. 'All time' keeps its preset, so the
      // dashboard still compares it with nothing.
      const revenueWindow = analyticsWindow(range, startedAt);
      const params: Record<string, string> = revenueWindow
        ? { fromDate: revenueWindow.start.toISOString(), toDate: revenueWindow.end.toISOString() }
        : {};
      const windowParams: Record<string, string> =
        revenueWindow && revenuePeriod !== 'all'
          ? {
              period: 'custom',
              startDate: revenueWindow.start.toISOString(),
              endDate: revenueWindow.end.toISOString(),
            }
          : { period: revenuePeriod };
      const [revRes, dashRes, breakdownRes] = await Promise.all([
        adminApi.getRevenueAnalytics(params),
        adminApi.getDashboard(windowParams).catch(() => ({ data: null })),
        adminApi.getRevenueBreakdown(windowParams).catch(() => ({ data: null })),
      ]);
      if (!isLatest()) return;
      setRevenue(revRes.data);
      const k = (dashRes.data?.kpi ?? dashRes.data) as {
        realMoneyRevenue?: number;
        realMoneyRevenueChange?: number;
        realMoneyRevenueByCurrency?: Array<{
          currency: string;
          net: number;
          previousNet: number;
          changePercent: number;
        }>;
        revenue?: number;
        revenueChange?: number;
        inGameCurrencyRevenue?: number;
        inGameCurrencyRevenueChange?: number;
      } | null;
      if (k) {
        setRevenueBreakdown({
          realMoneyByCurrency: k.realMoneyRevenueByCurrency ?? [],
          realMoney: k.realMoneyRevenue ?? k.revenue ?? 0,
          realMoneyChange: k.realMoneyRevenueChange ?? k.revenueChange ?? 0,
          inGame: k.inGameCurrencyRevenue ?? 0,
          inGameChange: k.inGameCurrencyRevenueChange ?? 0,
        });
      } else {
        // Don't leave the previous range's cards under the new range's label.
        setRevenueBreakdown(null);
      }
      const bd = breakdownRes?.data as {
        byClassification?: Array<{
          classification: string;
          gross: number;
          refunded: number;
          net: number;
          txnCount: number;
          refundedTxnCount: number;
        }>;
      } | null;
      setRevenueByClassification(bd?.byClassification ?? []);
      setRevenueShown({ range, asOf: startedAt });
    } catch {
      if (!isLatest()) return;
      message.error('Failed to load revenue analytics');
    } finally {
      if (isLatest()) setRevenueLoading(false);
    }
  }, [revenuePeriod, revenueCustomRange, beginRevenueRequest]);

  const fetchSubscriptions = useCallback(async () => {
    setSubLoading(true);
    try {
      const [subsRes, summaryRes] = await Promise.all([
        adminApi.getActiveSubscriptions({ limit: 50 }),
        adminApi.getSubscriptionSummary(),
      ]);
      setSubscriptions(
        subsRes.data?.subscriptions ||
          subsRes.data?.data ||
          (Array.isArray(subsRes.data) ? subsRes.data : [])
      );
      setSubSummary(summaryRes.data);
    } catch {
      message.error('Failed to load subscriptions');
    } finally {
      setSubLoading(false);
    }
  }, []);

  const fetchTesters = useCallback(async () => {
    setTestersLoading(true);
    try {
      const res = await adminApi.listTesters();
      setTesters(Array.isArray(res.data) ? res.data : []);
    } catch {
      message.error('Failed to load testers');
    } finally {
      setTestersLoading(false);
    }
  }, []);

  const handleAddTester = async (values: Record<string, string>) => {
    try {
      await adminApi.addTester(values as never);
      message.success('Tester added');
      setAddTesterModal(false);
      testerForm.resetFields();
      fetchTesters();
    } catch {
      message.error('Failed to add tester');
    }
  };

  const handleRemoveTester = async (id: string) => {
    try {
      await adminApi.removeTester(id);
      message.success('Tester removed');
      fetchTesters();
    } catch {
      message.error('Failed to remove tester');
    }
  };

  const handleToggleTester = async (id: string) => {
    try {
      await adminApi.toggleTester(id);
      fetchTesters();
    } catch {
      message.error('Failed to toggle tester');
    }
  };

  useEffect(() => {
    fetchPurchases();
  }, [fetchPurchases]);

  // Re-fetch revenue when the applied range changes. Keyed on the tab, not on
  // loaded data: a range applied while the first load is still running (or
  // after it failed) must be fetched too, which also makes that load stale.
  useEffect(() => {
    if (activeTab === 'revenue') fetchRevenue();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revenuePeriod, revenueCustomRange]);

  const [currentFilters, setCurrentFilters] = useState<Record<string, string | number | boolean>>(
    {}
  );
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('DESC');

  // Auto-refresh — re-fetches the currently visible tab's data using the
  // existing filters / period. Other tabs are not touched so users don't pay
  // backend cost for tabs they aren't looking at.
  const refreshActiveTab = useCallback(() => {
    if (activeTab === 'purchases') {
      fetchPurchases(page, currentFilters, pageSize);
    } else if (activeTab === 'revenue') {
      fetchRevenue();
    } else if (activeTab === 'subscriptions') {
      fetchSubscriptions();
    }
    // testers tab is admin-config — no auto-refresh
  }, [activeTab, page, pageSize, currentFilters, fetchPurchases, fetchRevenue, fetchSubscriptions]);
  useAutoRefresh(refreshActiveTab, autoRefresh, refreshInterval);

  const handleSearch = (values: Record<string, unknown>) => {
    const filters: Record<string, string | number | boolean> = {};
    if (values.orderId) filters.orderId = String(values.orderId);
    if (values.userId) filters.userId = String(values.userId);
    if (values.userName) filters.userName = String(values.userName);
    if (values.status) filters.status = String(values.status);
    if (values.platform) filters.platform = String(values.platform);
    if (values.purchaseType) filters.purchaseType = String(values.purchaseType);
    if (values.source) filters.source = String(values.source);
    if (values.classification) filters.classification = String(values.classification);
    const dateRange = values.dateRange as [Dayjs, Dayjs] | undefined;
    if (dateRange?.[0]) filters.startDate = dateRange[0].format('YYYY-MM-DD');
    if (dateRange?.[1]) filters.endDate = dateRange[1].format('YYYY-MM-DD');
    filters.sortBy = sortBy;
    filters.sortOrder = sortOrder;
    setCurrentFilters(filters);
    fetchPurchases(1, filters);
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleTableChange = (pagination: any, _filters: any, sorter: any) => {
    const newPage = pagination.current || 1;
    const newSize = pagination.pageSize || pageSize;
    let filters = { ...currentFilters };

    if (sorter.field || sorter.columnKey) {
      const newSortBy = String(sorter.field || sorter.columnKey);
      const newOrder = sorter.order === 'ascend' ? 'ASC' : 'DESC';
      setSortBy(newSortBy);
      setSortOrder(newOrder as 'ASC' | 'DESC');
      filters = { ...filters, sortBy: newSortBy, sortOrder: newOrder };
    }

    if (newSize !== pageSize) setPageSize(newSize);
    fetchPurchases(newPage, filters, newSize);
  };

  const purchaseColumns = [
    {
      title: 'Order ID',
      dataIndex: 'orderId',
      key: 'orderId',
      width: 140,
      ellipsis: true,
      render: (v: string) =>
        v ? (
          <Tooltip title={v}>
            <Text copyable={{ text: v }} style={{ fontSize: 12 }}>
              {v.slice(0, 16)}...
            </Text>
          </Tooltip>
        ) : (
          '-'
        ),
    },
    {
      title: 'User',
      key: 'user',
      width: 180,
      render: (_: unknown, record: Purchase) => (
        <Space direction="vertical" size={0}>
          <a onClick={() => push(`/players/${record.userId}`)}>
            <Text strong style={{ fontSize: 12 }}>
              {record.userDisplayName || 'Unknown'}
            </Text>
          </a>
          <Tooltip title={record.userId}>
            <Text type="secondary" copyable={{ text: record.userId }} style={{ fontSize: 11 }}>
              {record.userId.slice(0, 8)}...
            </Text>
          </Tooltip>
        </Space>
      ),
    },
    {
      title: 'Product',
      dataIndex: 'productId',
      key: 'productId',
      width: 140,
      ellipsis: true,
      sorter: true,
      render: (v: string) => {
        const isDailyDeal = v?.startsWith('daily_deal_');
        return (
          <Space direction="vertical" size={0}>
            <Text style={{ fontSize: 12 }}>{v}</Text>
            {isDailyDeal && (
              <Tag color="orange" style={{ fontSize: 10 }}>
                Daily Deal
              </Tag>
            )}
          </Space>
        );
      },
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      width: 130,
      sorter: true,
      render: (_: unknown, record: Purchase) => {
        if (record.amount == null) return '-';
        const amt = Number(record.amount);
        const cur = (record.currency || '').toUpperCase();
        const CURRENCY_COLORS: Record<string, string> = {
          INR: '#3f8600',
          USD: '#3f8600',
          EUR: '#3f8600',
          GBP: '#3f8600',
          COINS: '#faad14',
          MATCHTOKENS: '#722ed1',
          EVOTOKENS: '#13c2c2',
          GEMS: '#eb2f96',
          PREMIUMCURRENCY: '#eb2f96',
        };
        return (
          <Text strong style={{ color: CURRENCY_COLORS[cur] }}>
            {formatMoney(amt, cur)}
          </Text>
        );
      },
    },
    {
      title: 'Store price',
      key: 'storePrice',
      width: 120,
      render: (_: unknown, record: Purchase) => (
        <Space size={4}>
          <Text>{formatStorePrice(record.storePriceMicros, record.storeCurrency)}</Text>
          {record.storeRegion && (
            <Tag style={{ color: '#8c8c8c', fontSize: 10 }}>{record.storeRegion}</Tag>
          )}
        </Space>
      ),
    },
    {
      title: 'Platform',
      dataIndex: 'platform',
      key: 'platform',
      width: 100,
      sorter: true,
      render: (v: string) => (
        <Tag color={PLATFORM_COLORS[v] || 'default'}>
          {(v || '').replace('_', ' ').toUpperCase()}
        </Tag>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'purchaseType',
      key: 'purchaseType',
      width: 100,
      sorter: true,
      render: (v: string) => <Tag>{(v || '').toUpperCase()}</Tag>,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 180,
      sorter: true,
      render: (v: string, record: Purchase) => (
        <Space direction="vertical" size={0}>
          <Tag color={STATUS_COLORS[v] || 'default'}>{(v || '').toUpperCase()}</Tag>
          {v === 'refunded' && record.refundedAt && (
            <Text type="secondary" style={{ fontSize: 11 }}>
              {formatIstRefundTime(record.refundedAt)}
            </Text>
          )}
        </Space>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'purchaseClassification',
      key: 'purchaseClassification',
      width: 80,
      render: (v: string) => (
        <Tag color={CLASSIFICATION_COLORS[v] || 'default'}>
          {CLASSIFICATION_LABELS[v] || v || 'Real'}
        </Tag>
      ),
    },
    {
      title: 'Date',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 160,
      sorter: true,
      defaultSortOrder: 'descend' as const,
      render: (v: string) => dayjs(v).format('DD MMM YYYY, hh:mm A'),
    },
    {
      title: '',
      key: 'actions',
      width: 50,
      fixed: 'right' as const,
      render: (_: unknown, record: Purchase) => (
        <Button
          type="link"
          size="small"
          icon={<EyeOutlined />}
          onClick={() => show('purchases', record.id)}
        />
      ),
    },
  ];

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Title level={3} style={{ margin: 0 }}>
          <ShoppingOutlined /> Purchases
        </Title>
        <Space>
          {canEditEconomy?.can === true && (
            <Popconfirm
              title="Ask Google for refunds from the last 30 days and mark them refunded here? Balances change only if feature.refund_revoke is ON."
              okText="Sync"
              overlayStyle={{ maxWidth: 360 }}
              onConfirm={syncGoogleRefunds}
              disabled={syncingRefunds}
            >
              <Button icon={<SyncOutlined />} loading={syncingRefunds} disabled={syncingRefunds}>
                Sync Google refunds
              </Button>
            </Popconfirm>
          )}
          <AutoRefreshToggle
            enabled={autoRefresh}
            onEnabledChange={setAutoRefresh}
            intervalMs={refreshInterval}
            onIntervalChange={setRefreshInterval}
          />
          <ExportButton
            options={[
              exportOptions.purchases,
              exportOptions.revenue,
              exportOptions.inGameCurrencyBurn,
            ]}
            buttonText="Export"
          />
        </Space>
      </Row>

      <Tabs
        activeKey={activeTab}
        items={[
          {
            key: 'purchases',
            label: (
              <span>
                <CreditCardOutlined /> Transactions
              </span>
            ),
            children: (
              <>
                {/* Filters */}
                <Card size="small" style={{ marginBottom: 16 }}>
                  <Form form={form} onFinish={handleSearch}>
                    <Row gutter={[12, 8]}>
                      <Col xs={24} sm={12} md={4}>
                        <Form.Item name="orderId" style={{ marginBottom: 0 }}>
                          <Input placeholder="Order ID" allowClear />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12} md={4}>
                        <Form.Item name="userId" style={{ marginBottom: 0 }}>
                          <Input placeholder="User ID" allowClear />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12} md={4}>
                        <Form.Item name="userName" style={{ marginBottom: 0 }}>
                          <Input placeholder="User Name" allowClear />
                        </Form.Item>
                      </Col>
                      <Col xs={12} sm={6} md={3}>
                        <Form.Item name="status" style={{ marginBottom: 0 }}>
                          <Select placeholder="Status" allowClear>
                            <Select.Option value="completed">Completed</Select.Option>
                            <Select.Option value="pending">Pending</Select.Option>
                            <Select.Option value="failed">Failed</Select.Option>
                            <Select.Option value="refunded">Refunded</Select.Option>
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col xs={12} sm={6} md={3}>
                        <Form.Item name="platform" style={{ marginBottom: 0 }}>
                          <Select placeholder="Platform" allowClear>
                            <Select.Option value="google_play">Google Play</Select.Option>
                            <Select.Option value="app_store">App Store</Select.Option>
                            <Select.Option value="in_app">In-App</Select.Option>
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col xs={12} sm={6} md={3}>
                        <Form.Item name="purchaseType" style={{ marginBottom: 0 }}>
                          <Select placeholder="Type" allowClear>
                            <Select.Option value="consumable">Consumable</Select.Option>
                            <Select.Option value="non_consumable">Non-Consumable</Select.Option>
                            <Select.Option value="subscription">Subscription</Select.Option>
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col xs={12} sm={6} md={3}>
                        <Form.Item name="source" style={{ marginBottom: 0 }}>
                          <Select placeholder="Source" allowClear>
                            <Select.Option value="daily_deal">Daily Deals</Select.Option>
                            <Select.Option value="iap">IAP Store</Select.Option>
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col xs={12} sm={6} md={3}>
                        <Form.Item name="classification" style={{ marginBottom: 0 }}>
                          <Select placeholder="Classification" allowClear>
                            <Select.Option value="real">Real</Select.Option>
                            <Select.Option value="sandbox">Sandbox</Select.Option>
                            <Select.Option value="internal_tester">Tester</Select.Option>
                            <Select.Option value="editor">Editor</Select.Option>
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12} md={4}>
                        <Form.Item name="dateRange" style={{ marginBottom: 0 }}>
                          <RangePicker style={{ width: '100%' }} />
                        </Form.Item>
                      </Col>
                    </Row>
                    <Row style={{ marginTop: 8 }}>
                      <Space>
                        <Button type="primary" htmlType="submit" icon={<SearchOutlined />}>
                          Search
                        </Button>
                        <Button
                          icon={<ClearOutlined />}
                          onClick={() => {
                            form.resetFields();
                            fetchPurchases(1);
                          }}
                        >
                          Reset
                        </Button>
                      </Space>
                    </Row>
                  </Form>
                </Card>

                <Table
                  loading={loading}
                  dataSource={purchases}
                  columns={purchaseColumns}
                  rowKey="id"
                  size="small"
                  scroll={{ x: 1100 }}
                  onChange={handleTableChange}
                  pagination={{
                    current: page,
                    total,
                    pageSize,
                    showSizeChanger: true,
                    pageSizeOptions: ['10', '20', '50', '100'],
                    showTotal: (t) => `${t} purchases`,
                  }}
                />
              </>
            ),
          },
          {
            key: 'revenue',
            label: (
              <span>
                <BarChartOutlined /> Revenue Analytics
              </span>
            ),
            children: (
              <div>
                <Row justify="end" style={{ marginBottom: 16 }}>
                  <AnalyticsDateRangeFilter
                    value={{
                      period: revenuePeriod as AnalyticsPeriod,
                      customRange: revenueCustomRange ?? undefined,
                    }}
                    shown={revenueShown}
                    onApply={(next: AnalyticsDateRange) => {
                      setRevenuePeriod(next.period);
                      setRevenueCustomRange(next.customRange ?? null);
                    }}
                  />
                </Row>

                {revenue ? (
                  <>
                    {/* Revenue Breakdown — real money vs in-game currency.
                        The two are intentionally separate: real money is
                        the USD coming in from app-store IAP; in-game
                        currency is coins/gems/tokens BURNED on offer
                        purchases (Daily Deals, currency swaps, etc.) and
                        is measured in the corresponding in-game units. */}
                    {revenueBreakdown && (
                      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                        <Col xs={24} sm={12}>
                          <Card
                            hoverable
                            onClick={() =>
                              revenueByClassification.length > 0 && setClassificationDrillOpen(true)
                            }
                            style={{
                              borderTop: '3px solid #52c41a',
                              cursor: revenueByClassification.length > 0 ? 'pointer' : 'default',
                            }}
                            styles={{ body: { padding: 20 } }}
                          >
                            {revenueBreakdown.realMoneyByCurrency.length > 0 ? (
                              <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                <Space size={4}>
                                  <span style={{ fontWeight: 600 }}>Revenue (Real Money)</span>
                                  <Tooltip title="Real cash from completed app-store IAP transactions. Sum of purchase_history.amount where status='completed' AND purchaseClassification='real'. One row per currency — multi-currency totals are never summed naively. Click for per-classification breakdown.">
                                    <InfoCircleOutlined
                                      style={{ color: '#bfbfbf', fontSize: 12 }}
                                    />
                                  </Tooltip>
                                </Space>
                                {revenueBreakdown.realMoneyByCurrency.map((row) => (
                                  <Space key={row.currency} size={8} align="center">
                                    <span
                                      style={{ color: '#52c41a', fontSize: 22, fontWeight: 600 }}
                                    >
                                      {formatMoney(row.net, row.currency)}
                                    </span>
                                    <span
                                      style={{
                                        fontSize: 13,
                                        color:
                                          row.changePercent > 0
                                            ? '#52c41a'
                                            : row.changePercent < 0
                                              ? '#ff4d4f'
                                              : '#8c8c8c',
                                      }}
                                    >
                                      {row.changePercent > 0
                                        ? '▲'
                                        : row.changePercent < 0
                                          ? '▼'
                                          : ''}{' '}
                                      {Math.abs(row.changePercent)}%
                                    </span>
                                  </Space>
                                ))}
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                  vs previous {revenuePeriod.replace(/_/g, ' ')}
                                  {revenueByClassification.length > 0 && (
                                    <>
                                      {' · '}
                                      <Text style={{ fontSize: 12 }} type="success">
                                        click for breakdown →
                                      </Text>
                                    </>
                                  )}
                                </Text>
                              </Space>
                            ) : (
                              // Legacy fallback — older backend without byCurrency
                              <>
                                <Statistic
                                  title={
                                    <Space size={4}>
                                      <span style={{ fontWeight: 600 }}>Revenue (Real Money)</span>
                                      <Tooltip title="Real cash from completed app-store IAP transactions. Sum of purchase_history.amount where status='completed' AND purchaseClassification='real'. Click for per-classification breakdown.">
                                        <InfoCircleOutlined
                                          style={{ color: '#bfbfbf', fontSize: 12 }}
                                        />
                                      </Tooltip>
                                    </Space>
                                  }
                                  value={formatMoney(revenueBreakdown.realMoney, 'INR')}
                                  valueStyle={{ color: '#52c41a', fontSize: 28 }}
                                  suffix={
                                    <span
                                      style={{
                                        fontSize: 13,
                                        color:
                                          revenueBreakdown.realMoneyChange > 0
                                            ? '#52c41a'
                                            : revenueBreakdown.realMoneyChange < 0
                                              ? '#ff4d4f'
                                              : '#8c8c8c',
                                        marginLeft: 8,
                                      }}
                                    >
                                      {revenueBreakdown.realMoneyChange > 0
                                        ? '▲'
                                        : revenueBreakdown.realMoneyChange < 0
                                          ? '▼'
                                          : ''}{' '}
                                      {Math.abs(revenueBreakdown.realMoneyChange)}%
                                    </span>
                                  }
                                />
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                  vs previous {revenuePeriod.replace(/_/g, ' ')}
                                  {revenueByClassification.length > 0 && (
                                    <>
                                      {' · '}
                                      <Text style={{ fontSize: 12 }} type="success">
                                        click for breakdown →
                                      </Text>
                                    </>
                                  )}
                                </Text>
                              </>
                            )}
                          </Card>
                        </Col>
                        <Col xs={24} sm={12}>
                          <Card
                            hoverable
                            style={{ borderTop: '3px solid #faad14' }}
                            styles={{ body: { padding: 20 } }}
                          >
                            <Statistic
                              title={
                                <Space size={4}>
                                  <span style={{ fontWeight: 600 }}>
                                    Revenue (In-Game Currency)
                                  </span>
                                  <Tooltip title="In-game currency BURNED on purchases (Daily Deals, deal currency swaps, etc.). Sum of abs(amount) of negative currency_transactions whose source is purchase/currency_purchase/deal_currency_purchase/deal_purchase. Mixed units across currencies (coins / gems / tokens) — treat as activity volume, not USD.">
                                    <InfoCircleOutlined
                                      style={{ color: '#bfbfbf', fontSize: 12 }}
                                    />
                                  </Tooltip>
                                </Space>
                              }
                              value={revenueBreakdown.inGame}
                              valueStyle={{ color: '#faad14', fontSize: 28 }}
                              suffix={
                                <span
                                  style={{
                                    fontSize: 13,
                                    color:
                                      revenueBreakdown.inGameChange > 0
                                        ? '#52c41a'
                                        : revenueBreakdown.inGameChange < 0
                                          ? '#ff4d4f'
                                          : '#8c8c8c',
                                    marginLeft: 8,
                                  }}
                                >
                                  {revenueBreakdown.inGameChange > 0
                                    ? '▲'
                                    : revenueBreakdown.inGameChange < 0
                                      ? '▼'
                                      : ''}{' '}
                                  {Math.abs(revenueBreakdown.inGameChange)}%
                                </span>
                              }
                            />
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              vs previous {revenuePeriod.replace(/_/g, ' ')} · mixed in-game units
                            </Text>
                          </Card>
                        </Col>
                      </Row>
                    )}

                    {/* Per-classification breakdown strip — sums net per
                        classification across all currencies in the window.
                        After the INR back-fill (June 2026) all Google rows
                        are INR; Apple rows carry their actual currency. */}
                    {revenueByClassification.length > 0 && (
                      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                        {(['real', 'sandbox', 'internal_tester', 'editor'] as const).map(
                          (clsName) => {
                            const meta: Record<string, { label: string; color: string }> = {
                              real: { label: 'Real', color: '#52c41a' },
                              sandbox: { label: 'Sandbox', color: '#bfbfbf' },
                              internal_tester: {
                                label: 'Internal Tester',
                                color: '#faad14',
                              },
                              editor: { label: 'Editor', color: '#1890ff' },
                            };
                            const m = meta[clsName];
                            // Sum across rows where classification matches — but
                            // group by currency so we render one figure per
                            // currency rather than a meaningless cross-sum.
                            const rows = revenueByClassification.filter(
                              (c) => c.classification === clsName
                            );
                            if (rows.length === 0) return null;
                            const totalTxn = rows.reduce((s, r) => s + r.txnCount, 0);
                            const totalRefundedTxn = rows.reduce(
                              (s, r) => s + r.refundedTxnCount,
                              0
                            );
                            return (
                              <Col xs={12} sm={6} key={clsName}>
                                <Card
                                  size="small"
                                  hoverable
                                  onClick={() => setClassificationDrillOpen(true)}
                                  style={{
                                    borderLeft: `3px solid ${m.color}`,
                                    cursor: 'pointer',
                                  }}
                                  styles={{ body: { padding: 12 } }}
                                >
                                  <Text style={{ fontSize: 12, color: m.color }}>{m.label}</Text>
                                  <div style={{ marginTop: 4 }}>
                                    {rows.map((row) => (
                                      <div
                                        key={row.currency ?? 'UNKNOWN'}
                                        style={{
                                          fontSize: 16,
                                          color: m.color,
                                          fontWeight: 600,
                                          lineHeight: 1.3,
                                        }}
                                      >
                                        {formatMoney(row.net, row.currency ?? 'INR')}
                                      </div>
                                    ))}
                                  </div>
                                  <Text type="secondary" style={{ fontSize: 11 }}>
                                    {totalTxn} txn
                                    {totalRefundedTxn > 0 ? ` · ${totalRefundedTxn} refunded` : ''}
                                  </Text>
                                </Card>
                              </Col>
                            );
                          }
                        )}
                      </Row>
                    )}

                    {/* Overview */}
                    <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                      <Col xs={12} sm={6}>
                        <Card size="small">
                          <Statistic
                            title="Total Transactions"
                            value={revenue.summary.totalTransactions}
                          />
                        </Card>
                      </Col>
                      <Col xs={12} sm={6}>
                        <Card size="small">
                          <Statistic
                            title="Unique Buyers"
                            value={revenue.summary.uniqueBuyers}
                            prefix={<UserOutlined />}
                          />
                        </Card>
                      </Col>
                      <Col xs={12} sm={6}>
                        <Card size="small">
                          <Statistic title="New Buyers" value={revenue.summary.newBuyers} />
                        </Card>
                      </Col>
                      <Col xs={12} sm={6}>
                        <Card size="small">
                          <Statistic title="Repeat Buyers" value={revenue.summary.repeatBuyers} />
                        </Card>
                      </Col>
                    </Row>

                    {/* Platform & Purchase Type Breakdown — both are
                        real-money (USD) breakdowns from purchase_history. */}
                    <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                      <Col xs={24} lg={12}>
                        <Card
                          size="small"
                          title={
                            <Space>
                              <span>By Platform</span>
                              <Tooltip title="Real-money revenue (USD) split by Google Play / App Store / etc.">
                                <InfoCircleOutlined style={{ color: '#bfbfbf', fontSize: 12 }} />
                              </Tooltip>
                            </Space>
                          }
                        >
                          <Table<{
                            platform: string;
                            currency?: string;
                            revenue: number;
                            transactions: number;
                          }>
                            dataSource={(
                              (Array.isArray(revenue.summary.byPlatform)
                                ? revenue.summary.byPlatform
                                : Object.entries(revenue.summary.byPlatform || {}).map(
                                    ([k, v]) => ({
                                      platform: k,
                                      ...(v as Record<string, unknown>),
                                    })
                                  )) as Array<{
                                platform: string;
                                currency?: string;
                                revenue: number;
                                transactions: number;
                              }>
                            ).filter((p) => p.transactions > 0)}
                            rowKey={(row) => `${row.platform}|${row.currency ?? 'UNKNOWN'}`}
                            pagination={false}
                            size="small"
                            columns={[
                              {
                                title: 'Platform',
                                dataIndex: 'platform',
                                render: (v: string) => (
                                  <Tag color={PLATFORM_COLORS[v] || 'default'}>
                                    {(v || '').replace('_', ' ').toUpperCase()}
                                  </Tag>
                                ),
                              },
                              {
                                title: 'Currency',
                                dataIndex: 'currency',
                                render: (v?: string) => <Tag>{v || 'INR'}</Tag>,
                              },
                              {
                                title: 'Revenue',
                                dataIndex: 'revenue',
                                align: 'right',
                                render: (v: number, row: { currency?: string }) => (
                                  <Text strong style={{ color: '#52c41a' }}>
                                    {formatMoney(v, row.currency ?? 'INR')}
                                  </Text>
                                ),
                              },
                              {
                                title: 'Transactions',
                                dataIndex: 'transactions',
                                align: 'right',
                              },
                            ]}
                          />
                        </Card>
                      </Col>
                      <Col xs={24} lg={12}>
                        <Card
                          size="small"
                          title={
                            <Space>
                              <span>By Purchase Type</span>
                              <Tooltip title="Real-money revenue (USD) split by IAP product category — consumable / non-consumable / subscription / etc.">
                                <InfoCircleOutlined style={{ color: '#bfbfbf', fontSize: 12 }} />
                              </Tooltip>
                            </Space>
                          }
                        >
                          <Table<{
                            type: string;
                            currency?: string;
                            revenue: number;
                            transactions: number;
                          }>
                            dataSource={(
                              (Array.isArray(revenue.summary.byPurchaseType)
                                ? revenue.summary.byPurchaseType
                                : Object.entries(revenue.summary.byPurchaseType || {}).map(
                                    ([k, v]) => ({
                                      type: k,
                                      ...(v as Record<string, unknown>),
                                    })
                                  )) as Array<{
                                type: string;
                                currency?: string;
                                revenue: number;
                                transactions: number;
                              }>
                            ).filter((p) => p.transactions > 0)}
                            rowKey={(row) => `${row.type}|${row.currency ?? 'UNKNOWN'}`}
                            pagination={false}
                            size="small"
                            columns={[
                              {
                                title: 'Type',
                                dataIndex: 'type',
                                render: (v: string) => <Tag>{(v || '').toUpperCase()}</Tag>,
                              },
                              {
                                title: 'Currency',
                                dataIndex: 'currency',
                                render: (v?: string) => <Tag>{v || 'INR'}</Tag>,
                              },
                              {
                                title: 'Revenue',
                                dataIndex: 'revenue',
                                align: 'right',
                                render: (v: number, row: { currency?: string }) => (
                                  <Text strong style={{ color: '#52c41a' }}>
                                    {formatMoney(v, row.currency ?? 'INR')}
                                  </Text>
                                ),
                              },
                              {
                                title: 'Transactions',
                                dataIndex: 'transactions',
                                align: 'right',
                              },
                            ]}
                          />
                        </Card>
                      </Col>
                    </Row>

                    {/* Time Series & Top Products */}
                    <Row gutter={[16, 16]}>
                      <Col xs={24} lg={14}>
                        <Card
                          size="small"
                          title={
                            <Space>
                              <span>Transactions Over Time</span>
                              <Tooltip title="Net revenue and transaction count per day, split by currency. Rows are never summed across currencies — nothing converts between them.">
                                <InfoCircleOutlined style={{ color: '#bfbfbf', fontSize: 12 }} />
                              </Tooltip>
                            </Space>
                          }
                        >
                          <Table<RevenueTimeSeries>
                            dataSource={revenue.timeSeries}
                            rowKey={(row) => `${row.period}|${row.currency ?? 'UNKNOWN'}`}
                            pagination={false}
                            size="small"
                            scroll={{ y: 300 }}
                            columns={[
                              { title: 'Period', dataIndex: 'period', width: 120 },
                              {
                                title: 'Currency',
                                dataIndex: 'currency',
                                width: 90,
                                render: (c?: string) => <Tag>{c || 'UNKNOWN'}</Tag>,
                              },
                              {
                                // The backend groups this series by (period x currency)
                                // and returns the currency per row; render it in that
                                // currency rather than assuming USD.
                                title: 'Revenue',
                                dataIndex: 'revenue',
                                align: 'right',
                                render: (v: number, r: RevenueTimeSeries) => (
                                  <Text strong style={{ color: '#52c41a' }}>
                                    {formatMoney(v, r.currency)}
                                  </Text>
                                ),
                              },
                              {
                                title: 'Txns',
                                dataIndex: 'transactions',
                                width: 70,
                                align: 'right',
                              },
                              {
                                title: 'Buyers',
                                dataIndex: 'uniqueBuyers',
                                width: 70,
                                align: 'right',
                              },
                            ]}
                          />
                        </Card>
                      </Col>
                      <Col xs={24} lg={10}>
                        <Card title="Top Products" size="small">
                          <Table<RevenueTopProduct>
                            dataSource={revenue.topProducts}
                            // productId alone is no longer unique — a product
                            // sold in two currencies is two rows.
                            rowKey={(r) => `${r.productId}|${r.currency ?? ''}`}
                            pagination={false}
                            size="small"
                            scroll={{ y: 300 }}
                            columns={[
                              {
                                title: 'Product',
                                dataIndex: 'productId',
                                ellipsis: true,
                                render: (v: string, r: RevenueTopProduct) => (
                                  <Space direction="vertical" size={0}>
                                    <Text strong style={{ fontSize: 12 }}>
                                      {r.productName || v}
                                    </Text>
                                    {v.startsWith('daily_deal_') && (
                                      <Tag color="orange" style={{ fontSize: 10 }}>
                                        Daily Deal
                                      </Tag>
                                    )}
                                  </Space>
                                ),
                              },
                              {
                                title: 'Currency',
                                dataIndex: 'currency',
                                width: 80,
                                render: (c?: string) => <Tag>{c || 'UNKNOWN'}</Tag>,
                              },
                              {
                                // One row per product AND currency — see
                                // RevenueAnalyticsResponseDto.topProducts.
                                title: 'Revenue',
                                dataIndex: 'revenue',
                                width: 120,
                                align: 'right',
                                render: (v: number, r: RevenueTopProduct) => (
                                  <Text strong style={{ color: '#52c41a' }}>
                                    {formatMoney(v, r.currency)}
                                  </Text>
                                ),
                              },
                              {
                                title: 'Txns',
                                key: 'count',
                                width: 50,
                                align: 'right',
                                render: (_: unknown, r: RevenueTopProduct) =>
                                  r.transactions ||
                                  (r as unknown as Record<string, number>).count ||
                                  0,
                              },
                            ]}
                          />
                        </Card>
                      </Col>
                    </Row>
                  </>
                ) : revenueLoading ? (
                  <Card loading />
                ) : (
                  <Card>No revenue data</Card>
                )}
              </div>
            ),
          },
          {
            key: 'subscriptions',
            label: (
              <span>
                <CreditCardOutlined /> Subscriptions
              </span>
            ),
            children: (
              <div>
                {subSummary && (
                  <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                    <Col xs={12} sm={6}>
                      <Card size="small">
                        <Statistic
                          title="Active Subscriptions"
                          value={subSummary.totalActiveSubscriptions}
                        />
                      </Card>
                    </Col>
                    <Col xs={12} sm={6}>
                      <Card size="small">
                        <Statistic
                          title="Sub Revenue"
                          value={Number(subSummary.totalRevenue)}
                          precision={0}
                          valueStyle={{ color: '#3f8600' }}
                        />
                      </Card>
                    </Col>
                    <Col xs={12} sm={6}>
                      <Card size="small">
                        <Statistic
                          title="Expiring This Week"
                          value={subSummary.expiringThisWeek}
                          valueStyle={
                            subSummary.expiringThisWeek > 0 ? { color: '#cf1322' } : undefined
                          }
                        />
                      </Card>
                    </Col>
                    <Col xs={12} sm={6}>
                      <Card size="small">
                        <Statistic
                          title="Expiring This Month"
                          value={subSummary.expiringThisMonth}
                        />
                      </Card>
                    </Col>
                  </Row>
                )}

                <Table<ActiveSubscription>
                  loading={subLoading}
                  dataSource={subscriptions}
                  rowKey="purchaseId"
                  size="small"
                  pagination={{ pageSize: 20 }}
                  columns={[
                    {
                      title: 'User',
                      dataIndex: 'userId',
                      width: 120,
                      render: (v: string) => (
                        <Tooltip title={v}>
                          <a onClick={() => push(`/players/${v}`)}>
                            <Text style={{ fontSize: 11 }}>{v.slice(0, 8)}...</Text>
                          </a>
                        </Tooltip>
                      ),
                    },
                    {
                      title: 'Product',
                      key: 'product',
                      render: (_: unknown, r: ActiveSubscription) => r.productName || r.productId,
                    },
                    {
                      title: 'Platform',
                      dataIndex: 'platform',
                      width: 100,
                      render: (v: string) => (
                        <Tag color={PLATFORM_COLORS[v] || 'default'}>
                          {(v || '').replace('_', ' ').toUpperCase()}
                        </Tag>
                      ),
                    },
                    {
                      title: 'Start',
                      dataIndex: 'startDate',
                      width: 130,
                      render: (v: string) => dayjs(v).format('DD MMM YYYY'),
                    },
                    {
                      title: 'Expiry',
                      dataIndex: 'expiryDate',
                      width: 130,
                      render: (v: string) => dayjs(v).format('DD MMM YYYY'),
                    },
                    {
                      title: 'Days Left',
                      dataIndex: 'daysRemaining',
                      width: 90,
                      render: (v: number) => (
                        <Tag color={v <= 7 ? 'red' : v <= 30 ? 'orange' : 'green'}>{v}d</Tag>
                      ),
                    },
                    {
                      title: 'Auto-Renew',
                      dataIndex: 'isAutoRenewing',
                      width: 100,
                      render: (v: boolean) => (
                        <Tag color={v ? 'green' : 'default'}>{v ? 'Yes' : 'No'}</Tag>
                      ),
                    },
                  ]}
                />
              </div>
            ),
          },
          {
            key: 'testers',
            label: (
              <span>
                <UserOutlined /> Testers
              </span>
            ),
            children: (
              <div>
                <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
                  <Text strong>Purchase Tester Registry</Text>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => setAddTesterModal(true)}
                  >
                    Add Tester
                  </Button>
                </Row>
                <Table
                  dataSource={testers}
                  loading={testersLoading}
                  rowKey="id"
                  size="small"
                  pagination={false}
                  columns={[
                    {
                      title: 'Platform',
                      dataIndex: 'platform',
                      width: 100,
                      render: (v: string) => <Tag>{v.toUpperCase()}</Tag>,
                    },
                    {
                      title: 'Identifier (User ID / Email)',
                      dataIndex: 'identifier',
                      render: (v: string) => (
                        <Text copyable style={{ fontSize: 12 }}>
                          {v}
                        </Text>
                      ),
                    },
                    {
                      title: 'Label',
                      dataIndex: 'label',
                      width: 120,
                      render: (v: string) => (
                        <Tag
                          color={v === 'qa_team' ? 'blue' : v === 'internal' ? 'purple' : 'cyan'}
                        >
                          {v.replace('_', ' ').toUpperCase()}
                        </Tag>
                      ),
                    },
                    { title: 'Notes', dataIndex: 'notes', ellipsis: true },
                    {
                      title: 'Active',
                      dataIndex: 'isActive',
                      width: 80,
                      render: (v: boolean, record: PurchaseTester) => (
                        <Switch
                          checked={v}
                          size="small"
                          onChange={() => handleToggleTester(record.id)}
                        />
                      ),
                    },
                    {
                      title: '',
                      width: 50,
                      render: (_: unknown, record: PurchaseTester) => (
                        <Popconfirm
                          title="Remove tester?"
                          onConfirm={() => handleRemoveTester(record.id)}
                        >
                          <Button type="link" danger size="small" icon={<DeleteOutlined />} />
                        </Popconfirm>
                      ),
                    },
                  ]}
                />
                <Modal
                  title="Add Purchase Tester"
                  open={addTesterModal}
                  onCancel={() => setAddTesterModal(false)}
                  onOk={() => testerForm.submit()}
                >
                  <Form form={testerForm} layout="vertical" onFinish={handleAddTester}>
                    <Form.Item
                      name="platform"
                      label="Platform"
                      rules={[{ required: true }]}
                      initialValue="any"
                    >
                      <Select>
                        <Select.Option value="any">Any</Select.Option>
                        <Select.Option value="apple">Apple</Select.Option>
                        <Select.Option value="google">Google</Select.Option>
                      </Select>
                    </Form.Item>
                    <Form.Item
                      name="identifier"
                      label="Identifier (User ID or Email)"
                      rules={[{ required: true }]}
                    >
                      <Input placeholder="e.g. user UUID or email" />
                    </Form.Item>
                    <Form.Item
                      name="label"
                      label="Label"
                      rules={[{ required: true }]}
                      initialValue="internal"
                    >
                      <Select>
                        <Select.Option value="qa_team">QA Team</Select.Option>
                        <Select.Option value="internal">Internal</Select.Option>
                        <Select.Option value="partner">Partner</Select.Option>
                      </Select>
                    </Form.Item>
                    <Form.Item name="notes" label="Notes">
                      <Input.TextArea rows={2} placeholder="Optional notes" />
                    </Form.Item>
                  </Form>
                </Modal>
              </div>
            ),
          },
        ]}
        onChange={(key) => {
          setActiveTab(key);
          if (key === 'revenue' && !revenue) fetchRevenue();
          if (key === 'subscriptions' && subscriptions.length === 0) fetchSubscriptions();
          if (key === 'testers' && testers.length === 0) fetchTesters();
        }}
      />

      {/* Per-classification drill-down — opened from the Real Money card
          or any of the 4 classification cards in the Revenue Analytics tab. */}
      <Modal
        title="Revenue by Classification"
        open={classificationDrillOpen}
        onCancel={() => setClassificationDrillOpen(false)}
        footer={null}
        width={860}
      >
        <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
          Only <strong>real</strong> classification flows into the headline Real Money revenue
          total. The other three are surfaced here so QA and product can spot leakage (e.g. a
          sandbox or editor build that managed to write to the production purchase ledger).
        </Text>
        <Table<(typeof revenueByClassification)[number]>
          size="small"
          dataSource={revenueByClassification}
          rowKey={(row) => `${row.classification}|${row.currency ?? 'UNKNOWN'}`}
          pagination={false}
          columns={[
            {
              title: 'Classification',
              dataIndex: 'classification',
              render: (v: string) => {
                const m: Record<string, { label: string; color: string }> = {
                  real: { label: 'Real', color: 'green' },
                  sandbox: { label: 'Sandbox', color: 'default' },
                  internal_tester: { label: 'Internal Tester', color: 'orange' },
                  editor: { label: 'Editor', color: 'blue' },
                };
                const entry = m[v] || { label: v, color: 'default' };
                return <Tag color={entry.color}>{entry.label}</Tag>;
              },
            },
            {
              title: 'Currency',
              dataIndex: 'currency',
              render: (v?: string) => <Tag>{v || 'INR'}</Tag>,
            },
            {
              title: 'Gross',
              onCell: () => ({ style: { whiteSpace: 'nowrap' } }),
              dataIndex: 'gross',
              align: 'right',
              render: (v: number, row: { currency?: string }) =>
                formatMoney(v, row.currency ?? 'INR'),
            },
            {
              title: 'Refunded',
              onCell: () => ({ style: { whiteSpace: 'nowrap' } }),
              dataIndex: 'refunded',
              align: 'right',
              render: (v: number, row: { currency?: string }) =>
                v > 0 ? (
                  <Text type="danger">-{formatMoney(v, row.currency ?? 'INR')}</Text>
                ) : (
                  formatMoney(0, row.currency ?? 'INR')
                ),
            },
            {
              title: 'Net',
              onCell: () => ({ style: { whiteSpace: 'nowrap' } }),
              dataIndex: 'net',
              align: 'right',
              render: (v: number, row: { classification: string; currency?: string }) => (
                <strong
                  style={{
                    color: row.classification === 'real' ? '#52c41a' : undefined,
                  }}
                >
                  {formatMoney(v, row.currency ?? 'INR')}
                </strong>
              ),
            },
            {
              title: 'Txns',
              dataIndex: 'txnCount',
              align: 'right',
            },
            {
              title: 'Refunded Txns',
              dataIndex: 'refundedTxnCount',
              align: 'right',
              render: (v: number) => (v > 0 ? <Text type="warning">{v}</Text> : 0),
            },
          ]}
          summary={(data) => {
            const rows = data as readonly (typeof revenueByClassification)[number][];
            // Per-currency totals — never sum across currencies.
            const byCurrency: Record<string, { gross: number; refunded: number; net: number }> = {};
            for (const r of rows) {
              const cur = r.currency ?? 'INR';
              if (!byCurrency[cur]) {
                byCurrency[cur] = { gross: 0, refunded: 0, net: 0 };
              }
              byCurrency[cur].gross += r.gross;
              byCurrency[cur].refunded += r.refunded;
              byCurrency[cur].net += r.net;
            }
            return (
              <>
                {Object.entries(byCurrency).map(([cur, totals]) => (
                  <Table.Summary.Row key={cur}>
                    <Table.Summary.Cell index={0}>
                      <strong>All ({cur})</strong>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={1} />
                    <Table.Summary.Cell index={2} align="right">
                      <strong style={{ whiteSpace: 'nowrap' }}>
                        {formatMoney(totals.gross, cur)}
                      </strong>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={3} align="right">
                      <strong style={{ whiteSpace: 'nowrap' }}>
                        {totals.refunded > 0 ? '-' : ''}
                        {formatMoney(totals.refunded, cur)}
                      </strong>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={4} align="right">
                      <strong style={{ whiteSpace: 'nowrap' }}>
                        {formatMoney(totals.net, cur)}
                      </strong>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={5} />
                    <Table.Summary.Cell index={6} />
                  </Table.Summary.Row>
                ))}
              </>
            );
          }}
        />
      </Modal>
    </div>
  );
};
